import { BookOpen, Code2, FileText, PencilLine } from 'lucide-react';
import { type CSSProperties, type ReactNode, useState } from 'react';
import { useFileDrop } from '../hooks/useFileDrop';
import type { Effort, Model, PermissionMode } from '../lib/settings';
import { Composer } from './Composer';
import { DropOverlay } from './DropOverlay';
import { LogoMark } from './LogoMark';
import { RevealText } from './RevealText';

const SUGGESTIONS: { label: string; icon: ReactNode; prompt: string }[] = [
  {
    label: 'Найти ошибки в коде',
    icon: <Code2 size={16} strokeWidth={1.8} />,
    prompt: 'Найди ошибки в этом коде: ',
  },
  {
    label: 'Пересказать текст',
    icon: <FileText size={16} strokeWidth={1.8} />,
    prompt: 'Перескажи коротко этот текст: ',
  },
  {
    label: 'Написать текст',
    icon: <PencilLine size={16} strokeWidth={1.8} />,
    prompt: 'Помоги написать ',
  },
  {
    label: 'Объяснить тему',
    icon: <BookOpen size={16} strokeWidth={1.8} />,
    prompt: 'Объясни простыми словами ',
  },
];

type Props = {
  draftScope: string;
  onSubmit?: (
    text: string,
    model: Model,
    permission: PermissionMode,
    attachments: string[],
    effort: Effort,
  ) => void | Promise<void>;
  submitting?: boolean;
  error?: string | null;
  /** Chat/Mac mode switch above the composer (admin only). */
  modeSwitch?: ReactNode;
  toolbarExtra?: ReactNode;
  mac?: boolean;
};

/**
 * New-chat workbench: one prompt, file drop, and task starters.
 */
export function EmptyState({
  draftScope,
  onSubmit,
  submitting,
  error,
  modeSwitch,
  toolbarExtra,
  mac,
}: Props) {
  const [starter, setStarter] = useState<string | null>(null);
  const { dragOver, droppedFiles, consumeDroppedFiles } = useFileDrop();

  return (
    <section className="relative flex min-h-0 flex-1 flex-col" aria-label="Новый чат">
      <DropOverlay visible={dragOver} />
      <div className="flex min-h-0 flex-1 flex-col md:justify-center">
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center md:mb-10 md:flex-none">
          <LogoMark className="mb-6 h-10 w-auto" motion={submitting ? 'think' : 'draw'} />
          <RevealText
            as="h1"
            text="Чем займёмся?"
            delay={80}
            step={90}
            className="font-display text-[34px] font-medium leading-[1.05] tracking-[-0.04em] md:text-[48px]"
          />
          <p
            className="mc-enter mt-3 max-w-[30ch] text-[15px] leading-relaxed text-muted-foreground"
            style={{ animationDelay: '320ms' }}
          >
            {mac ? 'Опишите, что сделать на Маке' : 'Опишите задачу или прикрепите файл'}
          </p>
        </div>
        <div className="mc-safe-bottom mc-safe-x mx-auto w-full max-w-3xl px-3 pb-3 md:px-8">
          {error && (
            <div
              className="mb-2 rounded-2xl border border-destructive/40 bg-destructive/5 px-3 py-2 text-[13px] text-destructive"
              role="alert"
            >
              {error}
            </div>
          )}
          {modeSwitch && <div className="mb-2 flex justify-center">{modeSwitch}</div>}
          <div
            className="mc-stagger mc-no-scrollbar -mx-3 mb-2 flex snap-x scroll-px-3 gap-2 overflow-x-auto px-3 pb-1 md:mx-0 md:flex-wrap md:justify-center md:overflow-visible md:px-0"
            style={{ '--stagger-base': '420ms' } as CSSProperties}
          >
            {SUGGESTIONS.map((s) => (
              <SuggestionPill
                key={s.label}
                label={s.label}
                icon={s.icon}
                prompt={s.prompt}
                onPick={setStarter}
                disabled={submitting}
              />
            ))}
          </div>
          <div className="mc-enter" style={{ animationDelay: '380ms' }}>
            <Composer
              draftScope={draftScope}
              onSubmit={onSubmit}
              disabled={submitting}
              draftText={starter}
              onDraftConsumed={() => setStarter(null)}
              externalFiles={droppedFiles.length > 0 ? droppedFiles : undefined}
              onExternalFilesConsumed={consumeDroppedFiles}
              toolbarExtra={toolbarExtra}
              placeholder={mac ? 'Что сделать на Маке?' : undefined}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function SuggestionPill({
  label,
  icon,
  prompt,
  onPick,
  disabled,
}: {
  label: string;
  icon: ReactNode;
  prompt: string;
  onPick: (prompt: string) => void;
  disabled: boolean | undefined;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onPick(prompt)}
      className="mc-press inline-flex min-h-11 shrink-0 snap-start items-center gap-2 whitespace-nowrap rounded-full border border-border bg-surface px-4 py-2 text-[14px] active:bg-subtle md:hover:bg-subtle disabled:opacity-60"
    >
      <span className="text-muted-foreground">{icon}</span>
      {label}
    </button>
  );
}
